import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SectionNav } from '../components/layout/SectionNav';
import { CewSiteFooter } from '../components/layout/CewSiteFooter';
import { DetailFilterBar } from '../components/detail/DetailFilterBar';
import { RatioChart } from '../components/detail/RatioChart';
import { DetailTable } from '../components/detail/DetailTable';
// IntroModal import removed 2026-07-02 (Georgetown feedback: no more
// first-visit interstitials across data pages).
import { LoadingState } from '../components/shared/LoadingState';
import { ErrorState } from '../components/shared/ErrorState';
import { TableSourceNote } from '../components/shared/TableSourceNote';
import { useMetroData } from '../hooks/useMetroData';
import { useUrlParamSeed } from '../lib/use-url-param-seed';
import { useDetailSelectionStore } from '../stores/detailSelectionStore';
import { slugifyGroupName } from '../lib/occupation-utils';
import { toCsv, type CsvColumn } from '../lib/csv';
import type { OccupationGroupAlignment } from '../types/metro';

/**
 * CSV columns for the Detail page Copy / Export action -- shape mirrors
 * the visible <DetailTable /> after the 2026-06-05 (Maria) relabel pass
 * so the exported file reads exactly like the on-screen breakdown.
 *
 * Columns (5):
 *   1. Springboard occupational group -- group.name
 *   2. Annual credentials awarded -- group.awardsSb
 *   3. Annual job openings        -- group.numOpeningsTotal
 *   4. Credentials-to-jobs ratio  -- group.ratio (2dp)
 *   5. Annual credential shortage -- group.shortage (CR-017: the truncated
 *                                    shortage, incl. 0, for a group in
 *                                    shortage; em-dash only for surplus,
 *                                    keyed off the DEC-056 status)
 *
 * Wired 2026-06-09 (Slack Luis + Maria) -- TableActions was previously
 * rendered without a getCsvData prop, so the buttons fired the
 * "Nothing to copy" warn-toast on every click. This serializer feeds
 * the filtered groups (same `filteredGroups` array DetailTable
 * renders) into TableActions so the export matches what's visible.
 */
const DETAIL_CSV_COLUMNS: ReadonlyArray<CsvColumn<OccupationGroupAlignment>> = [
  { label: 'Springboard occupational group', value: (g) => g.name },
  { label: 'Annual credentials awarded', value: (g) => g.awardsSb },
  { label: 'Annual job openings', value: (g) => g.numOpeningsTotal },
  { label: 'Credentials-to-jobs ratio', value: (g) => g.ratio.toFixed(2) },
  {
    label: 'Annual credential shortage',
    // CR-017: key off the DEC-056 status, not the shortage value. A group in
    // shortage exports its (truncated, possibly-zero) shortage; a surplus
    // group exports an em-dash. Raw number (no formatNumber) keeps the CSV
    // field comma-free.
    value: (g) => (g.status === 'surplus' ? '—' : g.shortage),
  },
];

/**
 * Metro Detail page — deep dashboard for a SINGLE metro area,
 * showing the per-occupational-group credentials-to-jobs breakdown.
 *
 * Wiring (detail-wire-real-data / DEC-057 Step 3; metro picker
 * reverted to single-select per DEC-060):
 *
 *   1. `useMetroData()` fetches all 55 metros, same hook the
 *      Map + Rankings + Compare pages use. No new backend call —
 *      `occupationGroups` is already in the existing /api/metros
 *      response (Step 2 / PR #25 ingested it).
 *
 *   2. Metro selection lives in this page's local state as a
 *      SINGLE id and is passed into `DetailFilterBar`. Default
 *      is empty -> empty state ("pick a metro from the filter bar
 *      to see its breakdown"). The PR #55 multi-select was
 *      reverted (DEC-060) because the chart + table downstream
 *      only consume one metro -- rendering the first selected
 *      metro from an N-metro picker was misleading.
 *
 *   3. The page filters `selectedMetro.occupationGroups` by the
 *      occupational-group dropdown + search box, then hands the
 *      filtered list to both <RatioChart> and <DetailTable> so
 *      they always see the same view of the data.
 *
 *   4. When a metro is selected, a "See providers" cross-tab
 *      link surfaces below the filter bar (Maria's 2026-05-26
 *      Slack ask #2). Link narrows further when an occupational
 *      group filter is also active.
 *
 * Layout (unchanged from the shell):
 *
 *   <SectionNav>          -- sticky top-[var(--h-topbar)]
 *   <header>              -- page heading + subtitle, max-w-1200
 *   <DetailFilterBar>     -- sticky top-[var(--offset-nav-stack)]
 *   <section>             -- 1/3 (chart) + 2/3 (table) on desktop,
 *                          stacked on mobile
 *   <CewSiteFooter>
 */
export default function MetroDetail() {
  const { metros, isLoading, error, retry } = useMetroData();
  // IMP-006 -- selections live in a zustand singleton so they survive
  // tab navigation (Maria 2026-06-01). Pulling fields + setters
  // individually keeps the subscription narrow; the page still
  // treats them like local useState pairs.
  const occupationalGroupId = useDetailSelectionStore((s) => s.occupationalGroupId);
  const setOccupationalGroupId = useDetailSelectionStore((s) => s.setOccupationalGroupId);
  // Single-select metro id. Empty string = no metro picked yet
  // (page renders the empty state). Matches the prototype design
  // intent + PR #51 shape. See DEC-060 for the multi-select revert.
  const metroAreaId = useDetailSelectionStore((s) => s.metroAreaId);
  const setMetroAreaId = useDetailSelectionStore((s) => s.setMetroAreaId);

  // URL param seeding for the Map / Rankings / Providers cross-tab
  // "View detail page" links (Maria 2026-05-26 ask #2 +
  // 2026-05-29 drawer nav hot-fix follow-up). Reads `?metroId=`
  // and pre-populates the picker once both the URL param AND the
  // metros list are ready. Defensive: silently ignores missing /
  // blank / non-existent ids -- a broken link must not break the
  // page (the user just sees the empty state, same as no link).
  //
  // Uses the shared useUrlParamSeed hook (DEC-061) which guarantees
  // one-shot per mount; user-made picks are never stomped by a
  // subsequent re-render or metros refresh.
  useUrlParamSeed('metroId', metros.length > 0, (urlMetroId) => {
    if (urlMetroId && metros.some((m) => m.id === urlMetroId)) {
      setMetroAreaId(urlMetroId);
    }
  });

  const selectedMetro = useMemo(
    () => (metroAreaId ? metros.find((m) => m.id === metroAreaId) : undefined),
    [metros, metroAreaId],
  );

  // IMP-018 (Luis 2026-06-09 ask -- mirrors IMP-017 on Providers).
  // Build a per-OG annual-credentials map for the selected metro so
  // the OG combobox in the filter bar can render (N) badges per
  // option. `awardsSb` is the credentials field -- the Detail page
  // is fundamentally about credentials, so badges read as "annual
  // credentials awarded in this OG for this metro" (Mayuresh
  // chose `awardsSb` over `numOpeningsSb` after seeing the
  // field-availability constraint: Detail's per-OG rows carry
  // `awardsSb` / `numOpeningsSb` / `numOpeningsOther` /
  // `numOpeningsTotal` -- no `num_sb` field exists at this granularity).
  //
  // Keyed by OG display name (matches `OccupationGroupAlignment.name`)
  // because that's the shape `OccupationGroupCombobox` expects
  // (`counts?: Record<string, number>` keyed by name, set up in
  // PR #97 / #99). Math.round so the badge is always an integer --
  // the underlying ingest already returns integers in practice but
  // the type is `number` so we guard against float drift.
  //
  // null when no metro is selected -- the combobox treats null the
  // same as omitted (no badges anywhere), which matches the
  // empty-state UX: badges would be meaningless without a metro
  // scope. OGs missing from `selectedMetro.occupationGroups` (each
  // metro carries a subset of the 22-OG universe) simply aren't
  // keys in the map, which the combobox renders as "no badge" --
  // distinct from `awardsSb=0`, which renders as a (0) badge and
  // stays clickable per Luis's spec.
  const ogCounts = useMemo<Readonly<Record<string, number>> | null>(() => {
    if (!selectedMetro || !selectedMetro.occupationGroups) return null;
    const map: Record<string, number> = {};
    for (const og of selectedMetro.occupationGroups) {
      map[og.name] = Math.round(og.awardsSb);
    }
    return map;
  }, [selectedMetro]);

  // Apply occupational-group filter to the selected metro's group
  // list. Empty selected metro -> empty result (the page renders an
  // empty-state above the chart/table grid instead).
  //
  // Free-text search was removed in IMP-003 (2026-06-01): the prior
  // "Search occupational groups..." input + OG select pair collapsed
  // into a single OccupationGroupCombobox (typeahead). The combobox
  // owns the query state internally and emits only the committed
  // group id via onChange -- the filter pipeline is now single-axis.
  const filteredGroups: ReadonlyArray<OccupationGroupAlignment> = useMemo(() => {
    if (!selectedMetro || !selectedMetro.occupationGroups) return [];
    return selectedMetro.occupationGroups.filter((g) => {
      return !occupationalGroupId || slugifyGroupName(g.name) === occupationalGroupId;
    });
  }, [selectedMetro, occupationalGroupId]);

  // Build the CSV serializer for the Copy / Export toolbar in
  // <DetailFilterBar>. Same `filteredGroups` array the table
  // renders, run through the column schema above. Empty when no
  // metro is selected so the "Nothing to copy" toast still fires
  // in the page's placeholder state -- the user gets a meaningful
  // signal instead of an empty CSV download.
  const getCsvData = useCallback(() => {
    if (!selectedMetro || filteredGroups.length === 0) return '';
    return toCsv(filteredGroups, DETAIL_CSV_COLUMNS);
  }, [selectedMetro, filteredGroups]);

  const showEmptyState = !isLoading && !error && !selectedMetro;

  // Build a query-string for the Providers cross-tab link. When a
  // metro is selected we always pass `metroId`; we only add
  // `groupId` when the user has narrowed to a specific occupational
  // group (otherwise the link offers "all providers for this metro").
  const providersHref = useMemo(() => {
    if (!metroAreaId) return null;
    const params = new URLSearchParams();
    params.set('metroId', metroAreaId);
    if (occupationalGroupId) params.set('groupId', occupationalGroupId);
    return `/providers?${params.toString()}`;
  }, [metroAreaId, occupationalGroupId]);

  return (
    <>
      <SectionNav />
      <div className="mx-auto max-w-page px-8 pt-8">
        <header className="mb-4 flex flex-col gap-2">
          <h1 className="text-[clamp(1.25rem,3vw,1.75rem)] font-bold text-navy">
            Explore a single metro area
          </h1>
          <p className="text-sm leading-[1.6] text-text-3">
            {selectedMetro
              ? `Showing the occupational-group breakdown for ${selectedMetro.name}.`
              : 'Springboard occupations, annual alignment by occupational group, and how this metro stacks up.'}
          </p>
        </header>
      </div>
      <DetailFilterBar
        occupationalGroupId={occupationalGroupId}
        onOccupationalGroupChange={setOccupationalGroupId}
        metroAreaId={metroAreaId}
        onMetroAreaIdChange={setMetroAreaId}
        metros={metros}
        getCsvData={getCsvData}
        ogCounts={ogCounts}
      />
      <div className="bg-[#FCFCFD]">
        {/* 2026-07-03 (V2 feedback follow-up): reviewers said the
            Detail table still didn't fit all 5 columns on a wide
            desktop without horizontal scroll. The table's column
            widths add up to ~930px; the previous max-w-page +
            3-column 1:2 grid gave the table only ~730px. Bumped
            the max-w to 1440px and made the grid a fixed-chart
            layout ("chart 280px, table takes rest") so the table
            reliably gets ~1030px+ of horizontal room even on the
            narrowest desktop viewport this max-w exposes. */}
        <section className="mx-auto max-w-page-wide px-8 pb-12 pt-6">
          {isLoading && <LoadingState label="Loading metros..." />}
          {error && (
            <ErrorState
              title="Cannot load metros"
              message={error.message}
              onRetry={retry}
            />
          )}
          {showEmptyState && (
            <div
              role="status"
              className="mx-auto max-w-md rounded-md border border-dashed border-border bg-surface p-10 text-center"
            >
              <p className="mb-2 text-base font-semibold text-text-1">
                Select a metro to begin
              </p>
              {/* 2026-07-02 (V2 feedback): copy tightened per the
                  Georgetown spec — describes the action ("select a
                  metro area") + what the user gets ("annual job
                  openings, credential shortages, and
                  credentials-to-jobs ratios"). */}
              <p className="text-sm text-text-3">
                Use the Metro Area dropdown above to select a given metro area
                and review the number of annual job openings, credential
                shortages, and credentials-to-jobs ratios.
              </p>
            </div>
          )}
          {!isLoading && !error && selectedMetro && (
            <>
              {providersHref && (
                <div className="mb-4 flex justify-end">
                  <Link
                    to={providersHref}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-blue hover:underline focus:outline-none focus-visible:underline"
                    data-testid="detail-see-providers-link"
                  >
                    {occupationalGroupId
                      ? 'See providers for this metro + group →'
                      : 'See all providers for this metro →'}
                  </Link>
                </div>
              )}
              {/* 2026-07-03 (V2 feedback follow-up): grid retooled
                  from 3-column (chart 1/3, table 2/3) to a fixed-
                  chart layout — chart pinned at 280px, table takes
                  the remaining `1fr`. This grid gives DetailTable a
                  wide `1fr` slot, but until CR-16 (2026-07-08) the
                  table itself still rendered at a hard-coded ~930px
                  (fixed per-column `width`) regardless of the slot's
                  actual width, so it still horizontal-scrolled on
                  some viewports (Maria "+" — see CLAUDE.md CR-16).
                  DetailTable now uses `min-width`-only columns on a
                  `w-full` table, so it fills this `1fr` slot and only
                  scrolls once the slot itself shrinks below the
                  columns' combined minimum. */}
              {/* CR-27 follow-up (2026-07-08 visual audit): the 2-column
                  split kicked in at `md` (768px), so on tablet the table
                  got only ~530px and its columns clipped/scrolled hard.
                  Hold the stacked (chart-over-table, both full-width)
                  layout until `lg` (1024px) so the table has real room
                  before it goes side-by-side with the chart. */}
              {/* TD-04 (DEC-075): minmax(0,1fr) not bare 1fr — bare fr has
                  min-width:auto and can blow out to the table's intrinsic
                  width (the CR-31 clip). The child's min-w-0 (#139) stays
                  as defense-in-depth; the track is now safe by itself. */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:min-h-[640px]">
                {/* --offset-filter-stack = Topbar (var(--h-topbar), CR-028/029)
                    + SectionNav 60 + DetailFilterBar 60 -- a responsive calc()
                    built on the DEC-075 single-owner --h-topbar CSS var
                    (constraint-ownership.test.tsx TD-08a/TD-08b), resolving to
                    194px at desktop widths. +4px breathing room so the chart
                    panel does not sit flush against the filter bar shadow.
                    CR-028/029 follow-up (2026-08-18): reads the CSS var
                    directly instead of a hardcoded JS-constant px value so it
                    stays in sync with the Topbar's real height below 1001px.
                    CR-27 follow-up fix: sticky must engage at the SAME
                    breakpoint as the 2-column grid (`lg`). At `md:sticky` the
                    chart pinned while the tablet layout was still stacked
                    single-column, so the full-width chart overlapped the table
                    scrolling beneath it. */}
                <div
                  className="lg:sticky lg:self-start"
                  style={{ top: 'calc(var(--offset-filter-stack) + 4px)' }}
                >
                  <RatioChart groups={filteredGroups} />
                </div>
                {/* CR-31 (2026-07-08 visual-audit find): `min-w-0` is REQUIRED
                    on this `1fr` grid column. Without it the column's implicit
                    `min-width:auto` lets it grow to the table's intrinsic
                    content width (~1014px), so on laptop the whole table pushed
                    past the viewport's right edge and DetailTable's inner
                    `overflow-x-auto` never engaged — the last column ("Annual
                    credential shortage") was clipped with no way to scroll to
                    it. `min-w-0` pins the column to its fractional share so the
                    inner scroller takes over. */}
                <div className="min-w-0">
                  <DetailTable
                    groups={filteredGroups}
                    metroName={selectedMetro.name}
                  />
                </div>
              </div>
              {/* 2026-07-02 (V2 feedback): shared source + methodology
                  note under the detail table. Same wording as Rankings
                  and Compare so citations read consistently. */}
              <TableSourceNote />
            </>
          )}
        </section>
      </div>
      <CewSiteFooter />

    </>
  );
}
